import { MotionConfig } from 'motion/react';
import { useMemo, useEffect } from 'react';
import * as amplitude from '@amplitude/analytics-browser';

import { MantineProvider, createTheme } from '@mantine/core';
import { ExperimentProvider } from './context/use-experiment';
import { UserContextProvider } from './context/use-user-context';
import { getEnv } from '../utils/env';
import { CADDIE_Z_INDEX } from '../constants/z-index';

import { SUPERBAR_ROOT_SELECTOR } from './constants/stores';
import { useRootElement } from '../context/root-element-context';
import '@mantine/core/styles.css';

const amplitudeKey = getEnv('AMPLITUDE_KEY');
const amplitudeProxyDomainName = getEnv('AMPLITUDE_PROXY_DOMAIN_NAME');

const getHtmlFontSize = (): number => {
  if (typeof window === 'undefined') {
    return 16; // Default font size for SSR
  }
  return parseFloat(getComputedStyle(document.documentElement).fontSize);
};

const htmlFontSize = getHtmlFontSize();
const scaleFixed16 = 16 / htmlFontSize;

/**
 * Function to get the root element where color scheme attribute should be applied
 * Returns #caddie-root instead of html to isolate the widget
 */
const getCaddieRootElement = (): HTMLElement | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (
    document.querySelector<HTMLElement>(SUPERBAR_ROOT_SELECTOR) ?? undefined
  );
};

export const Providers = ({ children }: { children: React.ReactNode }) => {
  const rootElement = useRootElement();

  const theme = useMemo(
    () =>
      createTheme({
        scale: scaleFixed16,
        components: {
          Portal: {
            defaultProps: {
              /**
               * Render Mantine portals (menus, tooltips, etc.) inside the shadow root
               * so they can see the encapsulated styles. Falls back to body when no
               * shadow root is present (e.g. superserve-ui).
               */
              target: rootElement ?? undefined,
            },
          },
          Menu: {
            styles: {
              dropdown: {
                backgroundColor: 'white',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '4px',
                zIndex: CADDIE_Z_INDEX.MENU, // Ensure menu appears above all other elements
                pointerEvents: 'auto', // Override inherited pointer-events: none from #caddie-root
              },
              item: {
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '14px',
                lineHeight: '20px',
                color: '#000',
                '&:hover': {
                  backgroundColor: '#f1f3f5',
                },
              },
            },
          },
          Tooltip: {
            styles: {
              tooltip: {
                backgroundColor: '#1a1a1a',
                color: '#fff',
              },
            },
          },
          ActionIcon: {
            styles: {
              root: {
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              },
            },
          },
        },
      }),
    [rootElement]
  );

  useEffect(() => {
    if (amplitudeKey && amplitudeProxyDomainName) {
      amplitude.init(amplitudeKey, {
        autocapture: true,
        serverUrl: `https://${amplitudeProxyDomainName}/amplitude/2/httpapi`,
        // logLevel: 4,
      });
    } else {
      amplitude.setOptOut(true);
    }

    return () => {
      amplitude.reset();
      amplitude.setUserId(undefined);
    };
  }, []);

  return (
    <MantineProvider
      theme={theme}
      getRootElement={() => rootElement ?? getCaddieRootElement()}
      cssVariablesSelector=":root"
      forceColorScheme="light"
    >
      <ExperimentProvider>
        <UserContextProvider>
          <MotionConfig reducedMotion="user">{children}</MotionConfig>
        </UserContextProvider>
      </ExperimentProvider>
    </MantineProvider>
  );
};
